import { useEffect, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import { PAGINATION } from "@miplatform/constants";
import { usePagination } from "@miplatform/hooks";
import { getRecentItems, groupArrayByKeyWithMetadata } from "@miplatform/utils";
import { Box, TablePagination, Typography } from "@mui/material";
import { GridOverlay } from "@mui/x-data-grid-premium";
import { MiChip } from "../MiChip/MiChip";
import { MiListCards } from "../MiListCards/MiListCards";
import { MiPlaceholderList } from "../MiPlaceholderList/MiPlaceholderList";
import { MiTable } from "../MiTable/MiTable";
import { MiListGroupedCard } from "./MiListGroupedCard/MiListGroupedCard";

/**
 * A simple wrapper component that renders a placeholder list within a grid overlay.
 * Used as the empty state component for data grids.
 *
 * @component
 * @param {Object} props - Props to pass through to MiPlaceholderList
 * @param {string} [props.icon] - MDI icon path for the empty state
 * @param {string} [props.text] - Main text to display in empty state
 * @param {string} [props.helperText] - Helper text to display below main text
 * @returns {JSX.Element} The rendered empty list component
 */
export function EmptyList(props) {
  return (
    <GridOverlay>
      <MiPlaceholderList {...props} />
    </GridOverlay>
  );
}

/**
 * A versatile list component that can display data in either table or card view with various configuration options.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.data - The data to be displayed in the list
 * @param {Function} [props.setData] - Function to update the data state
 * @param {Function} [props.dataProvider] - Function to fetch data for the list
 * @param {Array} [props.columns] - Column configurations for table view
 * @param {boolean} [props.loading] - Loading state of the list
 * @param {Array} [props.tabs] - Array of tab configurations
 * @param {string} [props.paginationMode='client'] - Mode of pagination ('client' or 'server')
 * @param {number} [props.defaultPageSize] - Default number of items per page
 * @param {any} [props.refresh] - Trigger to refresh the list
 * @param {boolean} [props.refreshPagination] - Trigger to refresh pagination
 * @param {Array} [props.pageSizeOptions] - Available page size options
 * @param {string} [props.viewType] - Type of view ('card' or 'table')
 * @param {Component} [props.CardComponent] - Custom card component for card view
 * @param {Object} [props.cardProps] - Props to pass to card components
 * @param {boolean} [props.hideListPagination] - Whether to hide pagination controls
 * @param {boolean} [props.showGroupCount] - Whether to show count in grouped view
 * @param {string | function} [props.groupedTitle] - Title for grouped view
 * @param {boolean} [props.groupedMode] - Whether to enable grouped mode
 * @param {string|Function} [props.groupedCriteria] - Criteria for grouping items
 * @param {string} [props.pinGroupKeyToTop=''] - Group key to pin to the top
 * @param {Object} [props.cardListContainerProps] - Props for card list container
 * @param {Function} [props.groupedDataSort] - Custom sort function for grouped data
 * @param {Object} [props.groupedParamsCriteria] - Additional criteria for grouping
 * @param {Object} [props.emptyListProps] - Props for empty state component
 * @param {string} [props.search] - Search query
 * @param {string} [props.testID] - Test ID for the component
 * @param {Function} [props.onRowClick] - Callback for row click events
 * @param {Array} [props.recentData=[]] - Array of recent items
 * @param {Function} [props.setRecentData] - Function to update recent data
 * @param {Function} [props.recentDataProvider] - Function to fetch recent data
 * @param {Function} [props.getRowActions] - Function to get row actions
 * @param {string} [props.entityName] - Name of the entity being displayed (ie "Users")
 *
 * @returns {JSX.Element} Rendered list component
 */
export function MiList({
  data,
  setData,
  dataProvider,
  columns,
  loading = false,
  paginationMode,
  defaultPageSize,
  refresh,
  refreshPagination,
  pageSizeOptions,
  viewType,
  CardComponent,
  cardProps,
  hideListPagination,
  showGroupCount,
  groupedTitle,
  groupedMode,
  groupedCriteria,
  pinGroupKeyToTop = "",
  cardListContainerProps,
  groupedDataSort,
  groupedParamsCriteria,
  search,
  testID,
  onRowClick,
  recentData = [],
  setRecentData,
  recentDataProvider,
  getRowActions,
  entityName,
  emptyListProps = {},
  ...rest
}) {
  const {
    rowCount,
    pageCount,
    filters,
    paginationModel,
    getPageData,
    handlePaginationModel,
    onSortChange,
    onFilterChange,
  } = usePagination({
    setData,
    setRecentData,
    getPageData: dataProvider,
    getPageRecentData: recentDataProvider,
    mode: paginationMode,
    defaultPageSize,
    refresh,
    refreshPagination,
    search,
  });

  const { classes, theme } = useStyles({ isEmpty: data.length === 0 });

  const emptyListDefaultProps = {
    text: "No records found",
    iconColor: theme.palette.text.primary,
  };

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      getPageData();
    }
  }, [refresh]);

  const actionIconStyle = { color: theme.palette.grey.medium };

  const renderTable = () => {
    if (data && columns && paginationMode === PAGINATION.SERVER) {
      return (
        <MiTable
          columns={columns}
          rows={data}
          pageSizeOptions={pageSizeOptions}
          paginationModel={paginationModel}
          pagination
          paginationMode={paginationMode}
          sortingMode={paginationMode}
          filterMode={paginationMode}
          onPaginationModelChange={handlePaginationModel}
          onSortModelChange={onSortChange}
          onFilterModelChange={onFilterChange}
          rowCount={rowCount}
          pages={pageCount}
          fetchData={getPageData}
          actionIconStyle={actionIconStyle}
          onRowClick={onRowClick}
          getRowActions={getRowActions}
          {...rest}
        />
      );
    }
    return (
      <MiTable
        columns={columns}
        rows={data}
        pagination
        fetchData={getPageData}
        onPaginationModelChange={handlePaginationModel}
        actionIconStyle={actionIconStyle}
        onFilterModelChange={onFilterChange}
        filterModel={filters}
        pageSizeOptions={pageSizeOptions}
        onRowClick={onRowClick}
        getRowActions={getRowActions}
        {...rest}
      />
    );
  };

  const renderCardView = () => {
    const { page, pageSize: rowsPerPage } = paginationModel;

    const pageData =
      paginationMode === PAGINATION.SERVER
        ? data
        : data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (
      page > 0 &&
      pageData.length === 0 &&
      data.length > 0 &&
      paginationMode === PAGINATION.CLIENT
    ) {
      handlePaginationModel({ ...paginationModel, page: 0 });
    }

    const handleChangePage = (_, newPage) => {
      handlePaginationModel({ ...paginationModel, page: newPage });
    };

    const handleChangeRowsPerPage = (e) => {
      handlePaginationModel({
        page: 0,
        pageSize: e.target.value,
      });
    };

    const serverRecentItems = recentData;
    const clientRecentItems = getRecentItems(data);
    const recentItems =
      paginationMode === PAGINATION.CLIENT
        ? clientRecentItems
        : serverRecentItems;

    const groupedData = groupedMode
      ? groupArrayByKeyWithMetadata(
          paginationMode === PAGINATION.SERVER ? data : pageData,
          groupedCriteria,
          groupedParamsCriteria
        )
      : {};

    const paginationCount =
      paginationMode === PAGINATION.SERVER ? rowCount : data.length;

    const sort = ([a], [b]) => {
      if (pinGroupKeyToTop) {
        if (a === pinGroupKeyToTop) return -1;
        if (b === pinGroupKeyToTop) return 1;
      }
      return a.toLowerCase().localeCompare(b.toLowerCase());
    };

    const sortGroupedData = (groupedDataObj) => {
      const entries = Object.entries(groupedDataObj);
      if (groupedDataSort) {
        return entries.sort(groupedDataSort);
      }
      if (paginationMode === PAGINATION.SERVER && !pinGroupKeyToTop) {
        return entries;
      }
      return entries.sort(sort);
    };

    return (
      <>
        <Box className={classes.cardListContainer} {...cardListContainerProps}>
          {groupedMode ? (
            <Box className={classes.cardGroupByContainer}>
              {sortGroupedData(groupedData).map(([title, data], index) => (
                <MiListGroupedCard
                  key={title}
                  testId={index}
                  title={
                    <Box display="flex" alignItems="center">
                      {showGroupCount && (
                        <MiChip
                          style={{
                            color: "white",
                            fontSize: theme.fontSize.small,
                            backgroundColor: theme.palette.grey.medium,
                            height: "18px",
                            marginRight: theme.spacing(2),
                          }}
                          size="small"
                          label={data.data.length}
                        />
                      )}
                      <Typography
                        variant="body2"
                        color="grey.dark"
                        fontWeight={theme.fontWeight.semiBold}
                        fontSize={theme.fontSize.small}
                        lineHeight={1}
                      >
                        {/* data.data is being passed to able to aggregate values in title */}
                        {groupedTitle ? groupedTitle(title, data.data) : title}
                      </Typography>
                    </Box>
                  }
                  renderCard={
                    <MiListCards
                      CardComponent={CardComponent}
                      cardProps={cardProps}
                      data={data.data}
                    />
                  }
                  data={data.data}
                  cardProps={cardProps}
                />
              ))}
            </Box>
          ) : (
            <MiListCards
              CardComponent={CardComponent}
              cardProps={cardProps}
              data={pageData}
              recentData={recentItems}
              entityName={entityName}
            />
          )}
        </Box>
        {data.length === 0 && !loading && (
          <Box className={classes.emptyListContainer}>
            <MiPlaceholderList
              {...{ ...emptyListDefaultProps, ...emptyListProps }}
            />
          </Box>
        )}
        {!hideListPagination && (
          <TablePagination
            className={classes.cardListPagination}
            component="div"
            count={paginationCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            slotProps={{
              select: {
                "data-testid": "mi-pagination-dropdown",
              },
              actions: {
                previousButton: {
                  "data-testid": "previous-page-button",
                },
                nextButton: {
                  "data-testid": "next-page-button",
                },
              },
            }}
          />
        )}
      </>
    );
  };

  return viewType !== "card" ? (
    <Box className={classes.miListBox} data-testid={testID}>
      {renderTable()}
    </Box>
  ) : (
    <Box className={classes.miListContent} data-testid={testID}>
      {renderCardView()}
    </Box>
  );
}

const useStyles = makeStyles()((theme, { isEmpty }) => ({
  rootContainer: {
    backgroundColor: theme.palette.common.white,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0%",
  },
  miListBox: {
    height: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  miListContent: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  cardListContainer: {
    overflowY: "auto",
    ...(!isEmpty && {
      padding: theme.spacing(2),
    }),
    flex: 1,
  },
  cardGroupByContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  emptyListContainer: {
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  groupTitle: {
    fontWeight: theme.fontWeight.bold,
    fontSize: theme.fontSize.medium,
  },
  cardListPagination: {
    overflow: "visible",
  },
}));
